import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Components — kept static; ProtectedRoute wraps every protected route
// and AuthCallback handles the OAuth hash on initial paint.
import AuthCallback from './components/AuthCallback';
import ProtectedRoute from './components/ProtectedRoute';

// Pages on the critical first-paint path: marketing site + auth flows.
// These ship in the main chunk so a cold visit to /, /login, /register,
// /forgot-password, /about, or /contact has zero round-trips beyond the
// main bundle.
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import About from './pages/About';
import Contact from './pages/Contact';

// Everything else is route-split. webpackChunkName comments give each
// lazy module a stable, readable filename in build/static/js/ instead of
// auto-generated numeric IDs — useful for triaging in DevTools and for
// confirming this split is actually working in audits.
const Portal = lazy(() => import(/* webpackChunkName: "portal" */ './pages/Portal'));
const ResetPassword = lazy(() => import(/* webpackChunkName: "reset-password" */ './pages/ResetPassword'));
const Apply = lazy(() => import(/* webpackChunkName: "apply" */ './pages/Apply'));
const Admin = lazy(() => import(/* webpackChunkName: "admin" */ './pages/Admin'));
const Dashboard = lazy(() => import(/* webpackChunkName: "dashboard" */ './pages/Dashboard'));
const Profile = lazy(() => import(/* webpackChunkName: "profile" */ './pages/Profile'));
const Directory = lazy(() => import(/* webpackChunkName: "directory" */ './pages/Directory'));
const Resources = lazy(() => import(/* webpackChunkName: "resources" */ './pages/Resources'));
const Events = lazy(() => import(/* webpackChunkName: "events" */ './pages/Events'));
const EventDetail = lazy(() => import(/* webpackChunkName: "event-detail" */ './pages/EventDetail'));
const Announcements = lazy(() => import(/* webpackChunkName: "announcements" */ './pages/Announcements'));
const Communications = lazy(() => import(/* webpackChunkName: "communications" */ './pages/Communications'));
const Unsubscribe = lazy(() => import(/* webpackChunkName: "unsubscribe" */ './pages/Unsubscribe'));
const Mentorship = lazy(() => import(/* webpackChunkName: "mentorship" */ './pages/Mentorship'));
const Support = lazy(() => import(/* webpackChunkName: "support" */ './pages/Support'));
const AdminPanel = lazy(() => import(/* webpackChunkName: "admin-panel" */ './pages/AdminPanel'));
const Workspace = lazy(() => import(/* webpackChunkName: "workspace" */ './pages/Workspace'));
const Governance = lazy(() => import(/* webpackChunkName: "governance" */ './pages/Governance'));
const Elections = lazy(() => import(/* webpackChunkName: "elections" */ './pages/Elections'));
const MentorshipMatch = lazy(() => import(/* webpackChunkName: "mentorship-match" */ './pages/MentorshipMatch'));
const ConsulateDirectory = lazy(() => import(/* webpackChunkName: "consulate-directory" */ './pages/consulate/ConsulateDirectory'));
const ConsulateAccount = lazy(() => import(/* webpackChunkName: "consulate-account" */ './pages/consulate/ConsulateAccount'));

// Branded fallback shown while a lazy chunk downloads. Wordmark in the
// brand serif (Libre Baskerville) so it inherits the typography already
// loaded by the main chunk — no logo asset request, no extra round-trip.
const RouteLoader = () => (
  <div
    className="min-h-screen flex items-center justify-center bg-ags-bg"
    role="status"
    aria-live="polite"
    aria-label="Loading page"
  >
    <span className="font-heading text-3xl tracking-wide text-ags-blue animate-pulse">
      AGSHK
    </span>
  </div>
);

const AppRouter = () => {
  const location = useLocation();

  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions by processing new session_id FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<RouteLoader />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/portal" element={<Portal />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/apply" element={<Apply />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="/admin" element={<Admin />} />

        {/* Protected Routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/dashboard/profile" element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        } />
        <Route path="/dashboard/directory" element={
          <ProtectedRoute requiredRole={['member', 'executive', 'admin']}>
            <Directory />
          </ProtectedRoute>
        } />
        <Route path="/dashboard/resources" element={
          <ProtectedRoute requiredRole={['member', 'executive', 'admin']}>
            <Resources />
          </ProtectedRoute>
        } />
        <Route path="/dashboard/events" element={
          <ProtectedRoute requiredRole={['member', 'executive', 'admin']}>
            <Events />
          </ProtectedRoute>
        } />
        <Route path="/dashboard/events/:eventId" element={
          <ProtectedRoute requiredRole={['member', 'executive', 'admin']}>
            <EventDetail />
          </ProtectedRoute>
        } />
        <Route path="/dashboard/announcements" element={
          <ProtectedRoute requiredRole={['member', 'executive', 'admin']}>
            <Announcements />
          </ProtectedRoute>
        } />
        <Route path="/dashboard/mentorship" element={
          <ProtectedRoute requiredRole={['member', 'executive', 'admin']}>
            <Mentorship />
          </ProtectedRoute>
        } />
        <Route path="/dashboard/support" element={
          <ProtectedRoute>
            <Support />
          </ProtectedRoute>
        } />

        {/* Phase 3 Routes */}
        <Route path="/dashboard/workspace" element={
          <ProtectedRoute requiredRole={['executive', 'admin']}>
            <Workspace />
          </ProtectedRoute>
        } />
        <Route path="/dashboard/communications" element={
          <ProtectedRoute requiredRole={['executive', 'admin']}>
            <Communications />
          </ProtectedRoute>
        } />
        <Route path="/dashboard/governance" element={
          <ProtectedRoute requiredRole={['member', 'executive', 'admin']}>
            <Governance />
          </ProtectedRoute>
        } />
        <Route path="/dashboard/elections" element={
          <ProtectedRoute requiredRole={['member', 'executive', 'admin']}>
            <Elections />
          </ProtectedRoute>
        } />
        <Route path="/dashboard/mentorship-match" element={
          <ProtectedRoute requiredRole={['member', 'executive', 'admin']}>
            <MentorshipMatch />
          </ProtectedRoute>
        } />

        {/* Consulate Routes: consulate staff only, and they cannot leave this area */}
        <Route path="/consulate" element={
          <ProtectedRoute requiredRole="consulate">
            <ConsulateDirectory />
          </ProtectedRoute>
        } />
        <Route path="/consulate/account" element={
          <ProtectedRoute requiredRole="consulate">
            <ConsulateAccount />
          </ProtectedRoute>
        } />

        {/* Admin Routes */}
        <Route path="/dashboard/admin" element={
          <ProtectedRoute requiredRole={['executive', 'admin']}>
            <AdminPanel />
          </ProtectedRoute>
        } />
      </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <div className="App min-h-screen bg-ags-bg">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
